import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import { loadLibraryKpis } from "reducers/PredictiveScoring";
import { getLibraryKpis, getAvailableKpis, doStoredLibraryKpisBelongToLibrary, doStoredAdvertiserDetailsBelongToLibrary } from "selectors/PredictiveScoring";
import { usePredictiveScoringEnabled } from "hooks/usePredictiveScoringEnabled";

import "./KpiSelector.css";

const MAX_OBJECTIVES = 4;

// Render the same caret-down arrow the other Library Settings dropdowns use, in place of
// react-select's default SVG chevron, so the KPI selector matches the rest of the form.
function DropdownIndicator() {
	return <i className="fas fa-caret-down kpi-selector-caret" />;
}

const SELECT_COMPONENTS = {
	IndicatorSeparator: () => null,
	DropdownIndicator,
};

// The parent form saves whatever payload we report through onKpiChange, so the selector owns its
// objective state and pushes the derived payload up — no ref/imperative handle needed.
function toKpiPayload(objectives) {
	return objectives
		.filter(row => row.value !== "")
		.map((row, idx) => ({ idKpi: Number(row.value), isPrimary: idx === 0 }));
}

function KpiSelector({ libraryId, onKpiChange }) {
	const dispatch = useDispatch();
	const isEnabled = usePredictiveScoringEnabled();
	const isEnabledFreshForThisLibrary = useSelector(state => doStoredAdvertiserDetailsBelongToLibrary(state, libraryId));
	const areKpisForThisLibrary = useSelector(state => doStoredLibraryKpisBelongToLibrary(state, libraryId));
	const storedConfiguredKpis = useSelector(getLibraryKpis);
	const availableKpis = useSelector(getAvailableKpis);
	const configuredKpis = areKpisForThisLibrary ? storedConfiguredKpis : [];

	const nextRowId = useRef(0);
	const newRow = useCallback((value = "") => {
		nextRowId.current += 1;
		return { rowId: nextRowId.current, value };
	}, []);

	const [objectives, setObjectives] = useState(() => [newRow()]);

	useEffect(() => {
		setObjectives([newRow()]);
	}, [libraryId, newRow]);

	useEffect(() => {
		if (isEnabled && libraryId && isEnabledFreshForThisLibrary) {
			dispatch(loadLibraryKpis(libraryId));
		}
	}, [dispatch, isEnabled, libraryId, isEnabledFreshForThisLibrary]);

	useEffect(() => {
		if (!areKpisForThisLibrary) { return; }

		const primaryKpi = configuredKpis.find(kpi => kpi.isPrimary);
		const othersKpi = configuredKpis.filter(kpi => !kpi.isPrimary);
		const loaded = [];
		if (primaryKpi) { loaded.push(newRow(String(primaryKpi.idScoringKpi))); }
		othersKpi.forEach(kpi => loaded.push(newRow(String(kpi.idScoringKpi))));

		// No KPIs configured for this library — fall back to a single empty row, same as a fresh open.
		setObjectives(loaded.length ? loaded : [newRow()]);
	}, [areKpisForThisLibrary, configuredKpis, newRow]);

	// Keep the parent's payload in sync with the current objectives — covers both the initial load
	// from configuredKpis and every user edit, so submit always saves what's on screen.
	useEffect(() => {
		if (onKpiChange) { onKpiChange(toKpiPayload(objectives)); }
	}, [objectives, onKpiChange]);

	const handleObjectiveChange = useCallback((rowId, selectedOption) => {
		const value = selectedOption ? String(selectedOption.value) : "";
		setObjectives(prev => prev.map((row) => {
			if (row.rowId !== rowId) { return row; }
			return { ...row, value };
		}));
	}, []);

	const handleRemoveObjective = useCallback((rowId) => {
		setObjectives(prev => prev.filter(row => row.rowId !== rowId));
	}, []);

	const handleAddObjective = useCallback(() => {
		if (objectives.length < MAX_OBJECTIVES) {
			setObjectives(prev => [...prev, newRow()]);
		}
	}, [objectives.length, newRow]);

	if (!isEnabled) {
		return null;
	}

	const selectedIds = objectives.filter(row => row.value !== "").map(row => row.value);

	const allOptions = availableKpis.map(kpi => ({
		value: kpi.idScoringKpi,
		label: kpi.displayName,
	}));

	return (
		<div className="e-form__control-wrapper has-label kpi-selector-section">
			<label className="e-form__control-label">
				KPI(s)
				<i className="fas fa-info-circle kpi-selector-tip" data-tip="Select a KPI to enable predictive scoring. This field is required when predictive scoring is enabled for the advertiser. The first KPI selected is the primary KPI used for predictive scoring." />
			</label>

			{objectives.map((row, index) => {
				const placeholder = index === 0 ? "Select a primary KPI" : "Select a secondary KPI";
				const optionsForRow = allOptions.filter(
					opt => String(opt.value) === row.value || !selectedIds.includes(String(opt.value))
				);
				const currentValue = optionsForRow.find(opt => String(opt.value) === row.value) || null;

				return (
					<div key={row.rowId} className="kpi-selector-row">
						<Select
							className="kpi-selector-select"
							value={currentValue}
							onChange={option => handleObjectiveChange(row.rowId, option)}
							options={optionsForRow}
							placeholder={placeholder}
							isClearable={false}
							components={SELECT_COMPONENTS}
						/>
						<button
							type="button"
							className="kpi-selector-remove"
							onClick={() => handleRemoveObjective(row.rowId)}
							disabled={objectives.length === 1 || (row.value !== "" && selectedIds.length === 1)}
							title="Remove KPI"
						>
							<i className="far fa-trash-alt" />
						</button>
					</div>
				);
			})}

			{objectives.length < MAX_OBJECTIVES && (
				<button
					type="button"
					className="kpi-selector-add"
					onClick={handleAddObjective}
				>
					+ Add another KPI
				</button>
			)}
		</div>
	);
}

KpiSelector.displayName = "KpiSelector";

export default KpiSelector;
